import { emitEvent } from "../Shared/Assets/src/tagmanager";
import { getModalById } from "../Shared/Modal/modal";
import { isNullOrWhiteSpace } from "../Shared/Utilities/string";

/**
 * Creates a placeholder element for the Vetstoria widget script to attach to.p
 * @param vetstoriaId ID of the Vetstoria unit to load the widget for.
 */
const createWidget = (
  vetstoriaId: string,
  vetstoriaSites: string,
  multipleSitesTitle: string,
  multipleSitesDescription: string
) => {
  const widget = document.createElement("div");
  widget.id = "oabp-widget";
  widget.setAttribute("data-domain", "booking.vetstoria.com");
  widget.setAttribute(
    "data-theme-customisation",
    "11263B,11263B,11263B,ffffff,B9D5DE,00263e,00263e,00263e,00263e,00263e"
  );

  if (!isNullOrWhiteSpace(vetstoriaSites)) {
    widget.setAttribute("data-sites-encoded", "false");
    widget.setAttribute("data-sites", vetstoriaSites);
    widget.setAttribute("description-message", multipleSitesDescription);
    widget.setAttribute("force-desktop-view", "false");
    widget.setAttribute("offset", "0");
    widget.setAttribute("redirect-on-complete", "");
    widget.setAttribute("title-message", multipleSitesTitle);
  } else {
    widget.setAttribute("data-auto-mobile-redirection", "true");
    widget.setAttribute("data-force-desktop-view", "true");
    widget.setAttribute("data-mobile-message", "Touch Here to Make an Appointment");
    widget.setAttribute("data-mode", "html5");
    widget.setAttribute("data-offset", "0");
    widget.setAttribute("data-site-hash", vetstoriaId);
  }

  return widget;
};

/**
 * Creates a script element to load Vetstoria, expects a placeholder element with correct settings
 * to be present in the DOM.
 */
const createScript = (groupScript = false) => {
  const script = document.createElement("script");
  script.src = groupScript
    ? "https://booking.vetstoria.com/js/oabp-widget-group-require.min.js"
    : "https://booking.vetstoria.com/js/oabp-widget-require.min.js";
  script.id = "vetstoria-widget-loading-script";

  return script;
};

/**
 * Initialize booking buttons to trigger Vetstoria booking modals.
 * @param button The button (link) element to initialize the Vetstoria booking widget for.
 */
const initializeVetstoriaBookingButton = (button: HTMLAnchorElement) => {
  if (button.getAttribute("data-vetstoria-booking-button-initialized") !== "true") {
    button.setAttribute("data-vetstoria-booking-button-initialized", "true");

    const vetstoriaId = button.dataset.vetstoriaId ?? "";

    const vetstoriaSites = button.dataset.sites ?? "";
    const vetstoriaSitesTitle = button.dataset.messageTitle ?? "";
    const vetstoriaSitesDescription = button.dataset.messageDescription ?? "";

    const modalId = button.getAttribute("aria-controls") ?? "";
    const modal = getModalById(modalId);

    if (vetstoriaId?.length && modal) {
      button.addEventListener("click", e => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const widgetContainer = modal.element.querySelector<HTMLElement>(
          "[data-booking-modal-content]"
        );

        if (widgetContainer) {
          widgetContainer.innerHTML = ""; // Clear previous content

          const widget = createWidget(
            vetstoriaId,
            vetstoriaSites,
            vetstoriaSitesTitle,
            vetstoriaSitesDescription
          );

          const script = createScript(!isNullOrWhiteSpace(vetstoriaSites));

          widgetContainer.appendChild(widget);
          widgetContainer.appendChild(script);
        }

        modal.open();
      });
    }
  }
};

htmx.onLoad(rootElement => {
  // Track general booking button clicks
  rootElement.querySelectorAll("[data-booking-button]").forEach(button =>
    button.addEventListener("click", e => {
      const target = e.currentTarget as HTMLAnchorElement;

      if (target.href) {
        const url = new URL(target.href);

        emitEvent(
          "Booking click",
          { bookingSource: url.hostname, bookingUrl: target.href },
          false
        );
      }
    })
  );

  // Vetstoria booking buttons
  // Note that the event listener added in initializeVetstoriaBookingButton() stops event
  // propagation, so make sure to add this listener last.
  rootElement
    .querySelectorAll<HTMLAnchorElement>("[data-vetstoria-booking-button]")
    .forEach(bookingButton => {
      initializeVetstoriaBookingButton(bookingButton);
    });
});
